/**
 * Common reusable Zod primitives.
 */
import { z } from 'zod';
/** ULID string format — used for opaque public IDs (e.g. status-page slugs). */
export const UlidSchema = z
    .string()
    .regex(/^[0-9A-HJKMNP-TV-Z]{26}$/i, 'must be a valid ULID');
/** UUID v4 — primary keys. */
export const UuidSchema = z.string().uuid();
/** ISO-8601 timestamp string. Accepts and parses to Date. */
export const IsoDateTimeSchema = z.string().datetime({ offset: true });
/** Money amount represented as integer cents (USD only in v1). */
export const MoneyCentsSchema = z
    .number()
    .int()
    .min(0)
    .describe('USD amount in cents (e.g. $30.00 = 3000)');
/**
 * Phone number — a dialable number plus an OPTIONAL extension in the same field.
 * The base number allows the usual formatting chars (`+ 0-9 ( ) space - .`), and
 * an extension may follow via `x`, `ext`, `ext.`, `extension`, `#`, or `,`
 * (case-insensitive), e.g. "(817) 410-3114 x123", "817-410-3114 ext. 5",
 * "+1 817 410 3114 #99". Stored verbatim; use `splitPhoneExtension` /
 * `phoneToTelHref` / `phoneDialString` to render or dial it.
 */
export const PHONE_REGEX = /^[+0-9().\-\s]{5,}(?:\s*(?:x|ext\.?|extension|#|,)\s*[0-9]{1,7})?$/i;
export const PhoneSchema = z
    .string()
    .min(7)
    .max(32)
    .regex(PHONE_REGEX, 'invalid phone number');
/**
 * Split a phone string into its dialable number and its extension (or null).
 * The extension is the trailing `x123` / `ext 123` / `#123` / `,123` token.
 */
export function splitPhoneExtension(raw) {
    if (!raw)
        return { number: '', ext: null };
    const m = raw.match(/(?:\s*(?:x|ext\.?|extension|#|,)\s*([0-9]{1,7}))\s*$/i);
    if (m && m.index != null) {
        return { number: raw.slice(0, m.index).trim(), ext: m[1] ?? null };
    }
    return { number: raw.trim(), ext: null };
}
/**
 * The dialable digits of a phone number (extension stripped) in a best-effort
 * E.164 form. US 10-digit → +1XXXXXXXXXX; already-international (leading +) kept;
 * otherwise the raw digits. For SMS `to:` and as the base of a `tel:` href.
 */
export function phoneDialString(raw) {
    const { number } = splitPhoneExtension(raw);
    const hadPlus = number.trim().startsWith('+');
    const digits = number.replace(/\D/g, '');
    if (!digits)
        return '';
    if (hadPlus)
        return `+${digits}`;
    if (digits.length === 10)
        return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1'))
        return `+${digits}`;
    return digits;
}
/**
 * An RFC-3966 `tel:` href for tap-to-call, carrying the extension as `;ext=` so
 * the dialer can pause-and-send it, e.g. `tel:+18174103114;ext=123`. Returns
 * null when there are no dialable digits.
 */
export function phoneToTelHref(raw) {
    if (!raw)
        return null;
    const dial = phoneDialString(raw);
    if (!dial)
        return null;
    const { ext } = splitPhoneExtension(raw);
    return ext ? `tel:${dial};ext=${ext}` : `tel:${dial}`;
}
/** Email — Zod's built-in. */
export const EmailSchema = z.string().email();
/** A pin on a floorplan — normalized 0..1 coordinates on the rendered PNG. */
export const FloorplanPinSchema = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    page_index: z.number().int().min(0).default(0),
});
/** Standard pagination envelope on list responses. */
export const PageMetaSchema = z.object({
    total: z.number().int().min(0),
    limit: z.number().int().min(1).max(200),
    offset: z.number().int().min(0),
});
/** Standard pagination query params. */
export const PageQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/** Helper to build a paginated list response schema. */
export function PageEnvelope(item) {
    return z.object({
        data: z.array(item),
        page: PageMetaSchema,
    });
}
//# sourceMappingURL=common.js.map